import { Component, computed, inject } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CtaAnalyticsDetailsKpisTileComponent } from '@cta-analytics-details/cta-analytics-details-kpis-tile/cta-analytics-details-kpis-tile.component';
import { AnalyticsFormatterFactory } from '@cta-analytics-dialog/cta-analytics-dialog-content/cta-analytics-table/cta-analytics-details/analytics-formatters/analytics-formatter-factory';
import { ICtaAnalyticsData } from '@cta-analytics-dialog/cta-analytics-dialog-content/cta-analytics-table/cta-analytics-table.interface';
import { CtaAnalyticsTableService } from '@cta-analytics-dialog/cta-analytics-dialog-content/cta-analytics-table/cta-analytics-table.service';
import { EMatchingType, EProcessStatus } from '@enums/campaign-analytics.enum';
import { ECampaignStatus } from '@enums/campaign.enum';
import { IAnalyticsNode } from '@interfaces/campaign-analytics.interface';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SideDialogComponent } from '@shared/components/side-dialog/side-dialog.component';
import { SocialContentTileComponent } from '@shared/components/social-content-tile/social-content-tile.component';
import { TextClampTooltipDirective } from '@shared/directives/text-clamp-tooltip.directive';
import { DateUtilitiesService } from '@utilities/date-utilities.service';
import { SocialUtilitiesService } from '@utilities/social-utilities.service';
import { differenceInMinutes } from 'date-fns';
import { v4 as uuidv4 } from 'uuid';
import { ICreatorSocialContentDialogData } from './creator-social-content-dialog.interface';

@Component({
  imports: [
    SocialContentTileComponent,
    SideDialogComponent,
    TranslateModule,
    CtaAnalyticsDetailsKpisTileComponent,
    MatTooltipModule,
    TextClampTooltipDirective,
  ],
  selector: 'app-creator-social-content-dialog',
  templateUrl: './creator-social-content-dialog.component.html',
})
export class CreatorSocialContentDialogComponent {
  public readonly kpiCardDataForKpisTile = computed(() => {
    const execution = this.dummyExecution();

    if (!execution.platformFeature) {
      return [];
    }

    const formatter = this.analyticsFormatterFactory.createFormatter(
      execution.platformFeature,
      execution,
      this.translate,
    );

    return formatter.generateKpiCardData({ ignoreExpectedKpi: true }).filter((kpi) => kpi.kpiKey !== 'mediaValue');
  });

  public readonly tileConfiguration = computed(() => this.data.tileConfiguration);
  public readonly dialogTitle = computed(() => {
    return (
      this.tileConfiguration().title ??
      this.tileConfiguration().description ??
      this.translate.instant(`shared.platformFeatures.${this.tileConfiguration().platformFeature}`)
    );
  });

  public readonly dummyExecution = computed(() => {
    const tileConfiguration = this.tileConfiguration();
    const socialContent = tileConfiguration.content?.socialContent;
    const executionDuration =
      !socialContent ||
      SocialUtilitiesService.isNonStreamPlatformFeature(socialContent?.platformFeature) ||
      !socialContent.executionTime ||
      !('end' in socialContent)
        ? undefined
        : differenceInMinutes(
            socialContent.end ? this.dateUtils.parseDateTimeFromApi(socialContent.end) : new Date(),
            this.dateUtils.parseDateTimeFromApi(socialContent.executionTime),
          );
    const socialContentDependentData = this.ctaAnalyticsTableService.getSocialContentDependentTableData(
      { isLive: tileConfiguration.isLive, platformFeature: tileConfiguration.platformFeature } as IAnalyticsNode,
      tileConfiguration.content,
    );

    return {
      campaignId: '',
      campaignProcessStatus: EProcessStatus.notRequired,
      campaignStatus: ECampaignStatus.draft,
      content: tileConfiguration.content,
      creatorId: '',
      ctaAnalytics: null,
      ctaTemplateId: '',
      details: {
        executionDuration,
        executionStartDate: socialContent?.executionTime
          ? this.dateUtils.formatDate(this.dateUtils.parseDateTimeFromApi(socialContent.executionTime))
          : null,
        executionStartTime: socialContent?.executionTime
          ? this.dateUtils.formatTime(this.dateUtils.parseDateTimeFromApi(socialContent.executionTime))
          : null,
        plannedDuration: null,
        plannedStartDate: null,
        plannedStartTime: null,
        sponsoredContentDuration: null,
        sponsoredContentStartDate: null,
        sponsoredContentStartTime: null,
      },
      duration: null,
      executionId: null,
      expectedKpis: [],
      id: uuidv4(),
      isLive: tileConfiguration.isLive,
      lastProcessed: this.dateUtils.parseDateTimeFromApi(tileConfiguration.content?.socialContent?.lastProcessed ?? ''),
      platformFeature: tileConfiguration.platformFeature,
      socialContentId: socialContent?.id ?? null,
      status: EMatchingType.matched,
      tileConfiguration,
      url: socialContentDependentData.url,
    } as ICtaAnalyticsData;
  });

  private readonly analyticsFormatterFactory = inject(AnalyticsFormatterFactory);
  private readonly data = inject<ICreatorSocialContentDialogData>(MAT_DIALOG_DATA);
  private readonly dateUtils = inject(DateUtilitiesService);
  private readonly translate = inject(TranslateService);
  private readonly ctaAnalyticsTableService = inject(CtaAnalyticsTableService);
}
